import { useState } from 'react';
import type { ReactNode, RefObject } from 'react';

import type { CaseDetailDto, Role } from '../../api/types';
import { fmtDate, fmtISO } from '../../lib/format';
import { shiftIso } from '../../lib/deadlines';
import { EXTEND_REASONS, EXTEND_REASON_OTHER } from '../../lib/stages';

/**
 * Вложенных модалок в макете нет (ui-case-card.md §13) — эти три собраны на
 * существующих классах, поверх основной модалки: `.modal-overlay.nested` + `.modal.modal-sm`.
 */

interface SubShellProps {
    overlayRef: RefObject<HTMLDivElement | null>;
    titleId: string;
    number: string;
    title: string;
    chip?: { text: string; tone?: 'ok' | 'warn' | 'hot' } | null;
    onCancel: () => void;
    body: ReactNode;
    actions: ReactNode;
}

function SubShell({ overlayRef, titleId, number, title, chip, onCancel, body, actions }: SubShellProps) {
    return (
        <div
            className="modal-overlay nested show"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={overlayRef}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onCancel();
            }}
        >
            <div className="modal modal-sm">
                <div className="modal-head">
                    <div className="mh-text">
                        <div className="m-id">{number}</div>
                        <h3 id={titleId}>{title}</h3>
                        <div className="m-chips">{chip ? <span className={chip.tone}>{chip.text}</span> : null}</div>
                    </div>
                    <button type="button" onClick={onCancel} aria-label="Закрыть">
                        ✕
                    </button>
                </div>
                <div className="modal-body">{body}</div>
                <div className="modal-actions">{actions}</div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ отправка */

export interface SendCaseModalProps {
    detail: CaseDetailDto;
    busy: boolean;
    overlayRef: RefObject<HTMLDivElement | null>;
    onCancel: () => void;
    onConfirm: (justified: boolean) => void;
}

/**
 * Подтверждение отправки ответа. Результат рассмотрения — ровно два значения,
 * они пишутся в `gms_cases.justified` (ТЗ §4).
 */
export function SendCaseModal({ detail, busy, overlayRef, onCancel, onConfirm }: SendCaseModalProps) {
    const item = detail.case;
    const [justified, setJustified] = useState('true');

    return (
        <SubShell
            overlayRef={overlayRef}
            titleId="gmsSendTitle"
            number={item.number}
            title="Отправить ответ заявителю?"
            chip={item.approved ? { text: 'Согласовано главным врачом', tone: 'ok' } : null}
            onCancel={onCancel}
            body={
                <>
                    <div className="kv">
                        <span className="k">Кому</span>
                        <span className="v">{item.applicant ?? 'заявитель не указан'}</span>
                        <span className="k">Результат рассмотрения</span>
                        <span className="v">
                            <select
                                className="act-select"
                                aria-label="Результат рассмотрения"
                                value={justified}
                                onChange={(event) => setJustified(event.target.value)}
                            >
                                <option value="true">Признано обоснованным</option>
                                <option value="false">Доводы не подтвердились</option>
                            </select>
                        </span>
                    </div>
                    <div className="modal-note">
                        После отправки дело закрывается: текст ответа станет недоступен для правки, а дело уйдёт из
                        списка открытых.
                    </div>
                </>
            }
            actions={
                <>
                    <button type="button" className="act-btn" onClick={onCancel}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="act-btn primary"
                        disabled={busy}
                        onClick={() => onConfirm(justified === 'true')}
                    >
                        📨 Отправить и закрыть дело
                    </button>
                </>
            }
        />
    );
}

/* --------------------------------------------------------------- назначение */

export interface AssignCaseModalProps {
    detail: CaseDetailDto;
    busy: boolean;
    overlayRef: RefObject<HTMLDivElement | null>;
    onCancel: () => void;
    onConfirm: (executorId: string | null, executorName: string) => void;
}

/** Назначение и переназначение исполнителя (ТЗ §2: только главврач). */
export function AssignCaseModal({ detail, busy, overlayRef, onCancel, onConfirm }: AssignCaseModalProps) {
    const item = detail.case;
    const [picked, setPicked] = useState<string | null>(null);
    const chosen = detail.executors.find((e) => e.id === picked) ?? null;

    return (
        <SubShell
            overlayRef={overlayRef}
            titleId="gmsAssignTitle"
            number={item.number}
            title="Кому передать дело"
            chip={item.executor_name ? { text: 'Сейчас: ' + item.executor_name } : { text: 'Без исполнителя', tone: 'warn' }}
            onCancel={onCancel}
            body={
                <>
                    <div className="kv">
                        <span className="k">Текущий исполнитель</span>
                        <span className="v">{item.executor_name ?? 'не назначен'}</span>
                    </div>
                    <div className="case-section">
                        <h4>Кому передать</h4>
                        {detail.executors.length === 0 ? (
                            <div className="detail-empty">В клинике нет активных исполнителей.</div>
                        ) : (
                            detail.executors.map((executor) => (
                                <div
                                    key={executor.id}
                                    className="list-row"
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={picked === executor.id}
                                    onClick={() => setPicked(executor.id)}
                                    onKeyDown={(event) => {
                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                        event.preventDefault();
                                        setPicked(executor.id);
                                    }}
                                >
                                    <span className="lr-title">{executor.full_name}</span>
                                    <span className="lr-exec">{executor.post && executor.post !== executor.full_name ? executor.post : 'исполнитель'}</span>
                                    <span className="lr-date">{'открыто ' + executor.open_cases}</span>
                                    {picked === executor.id ? <span className="badge open">выбран</span> : null}
                                    {executor.id === item.executor_id ? (
                                        <span className="badge closed">текущий</span>
                                    ) : null}
                                </div>
                            ))
                        )}
                    </div>
                </>
            }
            actions={
                <>
                    <button type="button" className="act-btn" onClick={onCancel}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="act-btn primary"
                        disabled={busy || !chosen || chosen.id === item.executor_id}
                        onClick={() => (chosen ? onConfirm(chosen.id, chosen.full_name) : undefined)}
                    >
                        Назначить исполнителя
                    </button>
                </>
            }
        />
    );
}

/* ---------------------------------------------------------------- продление */

/** Дата после продления. Считаем по календарным дням, а не через UTC: иначе срок уедет на сутки. */
function plus30(iso: string | null): string {
    const day = fmtISO(iso);
    if (!day) return '—';
    return fmtDate(shiftIso(day, 30));
}

export interface ExtendCaseModalProps {
    detail: CaseDetailDto;
    role: Role | null;
    busy: boolean;
    overlayRef: RefObject<HTMLDivElement | null>;
    onCancel: () => void;
    onConfirm: (reason: string, comment: string) => void;
}

/** Продление срока на 30 дней: пять оснований, «Иное» требует текста (ТЗ §4). */
export function ExtendCaseModal({ detail, role, busy, overlayRef, onCancel, onConfirm }: ExtendCaseModalProps) {
    const item = detail.case;
    const [reason, setReason] = useState<string>(EXTEND_REASONS[0]);
    const [comment, setComment] = useState('');
    const needComment = reason === EXTEND_REASON_OTHER;
    const ready = !needComment || comment.trim().length > 0;

    return (
        <SubShell
            overlayRef={overlayRef}
            titleId="gmsExtendTitle"
            number={item.number}
            title="Продлить срок рассмотрения"
            chip={{ text: 'Сейчас до ' + (fmtDate(item.legal_deadline) || 'не задан'), tone: 'warn' }}
            onCancel={onCancel}
            body={
                <>
                    <div className="case-section" role="radiogroup" aria-label="Основание продления">
                        <h4>Основание продления</h4>
                        {EXTEND_REASONS.map((value) => (
                            <div key={value} className="check-item">
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={reason === value}
                                    className={'check-box' + (reason === value ? ' on' : '')}
                                    onClick={() => setReason(value)}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="12"
                                        height="12"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth="3.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </button>
                                <span className="check-text">{value}</span>
                            </div>
                        ))}
                    </div>

                    {needComment ? (
                        <div className="comment-box">
                            <input
                                type="text"
                                value={comment}
                                placeholder="Опишите основание"
                                aria-label="Основание продления"
                                onChange={(event) => setComment(event.target.value)}
                            />
                        </div>
                    ) : null}

                    <div className="modal-note">
                        {'Срок сдвигается на 30 дней: до ' +
                            plus30(item.legal_deadline) +
                            '. ' +
                            (role === 'chief'
                                ? 'Главный врач продлевает срок сразу, утверждение не требуется.'
                                : 'Продление требует утверждения главным врачом.')}
                    </div>
                </>
            }
            actions={
                <>
                    <button type="button" className="act-btn" onClick={onCancel}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="act-btn primary"
                        disabled={busy || !ready}
                        onClick={() => onConfirm(reason, comment.trim())}
                    >
                        ⏳ Продлить срок
                    </button>
                </>
            }
        />
    );
}
